import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Caselle } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Scelta, Spunta } from '../../componenti/base.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import { SceltaStanza } from '../../componenti/stanze.tsx'
import { giornoInParole, oggiARoma } from '../../componenti/calendario/date.ts'
import { durataLeggibile } from '../../servizi/mediazioni.ts'

/**
 * Pezzi dell'agenda della sede (559-560): forme dei dati di GET /agenda, dettagli di un impegno e di un
 * appuntamento, il modulo di un impegno (riunione, prenotazione, turno, attivita', evento) e quello
 * dell'indisponibilita'. Le regole le applica l'API: qui si dice che cosa succede e si chiede conferma dove
 * l'API avvisa («Fissalo lo stesso» per la sede chiusa; la stanza occupata invece blocca).
 */

export const STRATI = ['appuntamenti', 'mediazioni', 'turni', 'stanze', 'riunioni', 'attivita', 'chiusure'] as const
export type Strato = (typeof STRATI)[number]
export const NOMI_STRATI: Record<Strato, string> = {
  appuntamenti: 'Appuntamenti', mediazioni: 'Mediazioni', turni: 'Turni', stanze: 'Stanze prenotate',
  riunioni: 'Riunioni e impegni', attivita: 'Attività', chiusure: 'Chiusure',
}

export type Conflitto = { codice: string; gravita: 'rosso' | 'arancione'; messaggio: string }
export type Impegno = {
  chiave: string; strato: Strato; tipo: string; id: string; giorno: string; dalle: string; alle: string
  tutto_il_giorno: boolean; titolo: string; dettaglio: string | null; risorsa_id: string | null
  risorsa_nome: string | null; persona_turno: string | null; persona_colonna: string | null; stato: string | null
  ricorrente: boolean
  conflitti: Conflitto[]; modificabile: boolean; trascinabile: boolean; occupato: boolean
  persona: { id: string; nome: string; cognome: string } | null
}
export type Sede = { id: string; nome: string }
export type RispostaAgenda = {
  finestra: { dal: string; al: string }
  sede: Sede | null; mia: boolean; sedi: Sede[]; strati: Strato[]
  stanze: { id: string; nome: string; capienza: number | null }[]
  persone: { chiave: string; tipo: 'utente' | 'mediatore'; nome: string; cognome: string; in_turno: boolean }[]
  impegni: Impegno[]
  stato_sede: unknown[] | null
  funzioni: { codice: string; nome: string }[]
  puo: {
    scrivere: boolean; turni: boolean; sedi_scrittura: Sede[]; sedi_turni: Sede[]; fissare_appuntamenti: boolean
    /** I propri impegni personali (563): chiunque abbia un'utenza. Gli inviti solo con `agenda.scrivi`. */
    propri: boolean; invitare: boolean
    /** Le agende delle sedi (`agenda.leggi`): senza, c'e' solo «La mia agenda». */
    sedi: boolean
  }
}

type TipoEvento = 'riunione' | 'attivita' | 'prenotazione' | 'turno' | 'evento'
const NOMI_TIPO: Record<TipoEvento | 'indisponibile', string> = {
  riunione: 'Riunione', attivita: 'Attività', prenotazione: 'Prenotazione di una stanza', turno: 'Turno',
  evento: 'Evento', indisponibile: 'Non disponibile',
}

/** Valori del modulo quando si apre dallo spazio vuoto o da «Nuovo impegno». */
export type InizialiEvento = {
  giorno: string; dalle?: string; sede_id: string | null; risorsa_id?: string; persona?: string
}

type Ripetizione = {
  frequenza: 'giornaliera' | 'settimanale' | 'mensile_giorno' | 'mensile_settimana'; intervallo: number
  giorni_settimana: number[] | null; fino_al: string | null; volte: number | null; salta_chiusure: boolean
}
type DettaglioEventoApi = {
  id: string; tipo: TipoEvento | 'indisponibile'; sede_id: string | null; sede_nome: string | null
  risorsa_id: string | null; risorsa_nome: string | null; attivita: string | null; progetto: string | null
  titolo: string | null; note: string | null; utente_id: string | null; mediatore_id: string | null
  persona: string | null; funzione_codice: string | null; funzione: string | null
  giorno: string; dalle: string | null; alle: string | null; tutto_il_giorno: boolean
  ripetizione: Ripetizione | null; ripetizione_in_parole: string | null; visibilita: 'sede' | 'partecipanti'
  stato: string; partecipanti: { id: string; nome: string; cognome: string }[]; creato_da: string | null
  volta: { giorno: string; dalle: string | null; alle: string | null; sostituto: string | null; modificata: boolean } | null
  volta_annullata: boolean
  puo_modificare: boolean
}

const GIORNI = [
  { codice: '1', nome: 'lun' }, { codice: '2', nome: 'mar' }, { codice: '3', nome: 'mer' }, { codice: '4', nome: 'gio' },
  { codice: '5', nome: 'ven' }, { codice: '6', nome: 'sab' }, { codice: '7', nome: 'dom' },
]
const FREQUENZE = [
  { valore: 'giornaliera', etichetta: 'Ogni giorno' },
  { valore: 'settimanale', etichetta: 'Ogni settimana' },
  { valore: 'mensile_giorno', etichetta: 'Ogni mese, nello stesso giorno' },
  { valore: 'mensile_settimana', etichetta: 'Ogni mese, nella stessa settimana (per esempio il secondo martedì)' },
]
const giornoIso = (g: string) => ((new Date(`${g}T12:00:00Z`).getUTCDay() + 6) % 7) + 1

// --- dettagli ----------------------------------------------------------------------------------------

/** Un appuntamento della scheda (560) dall'agenda: il nome e il motivo solo a chi segue la scheda. */
export function DettaglioAppuntamento({ id, chiudi }: { id: string; chiudi: () => void }) {
  const [d, setD] = useState<{
    giorno: string; dalle: string; alle: string; durata_minuti: number; stato: string; sede_nome: string | null
    risorsa_nome: string | null; responsabile: string | null; persona: { id: string; nome: string; cognome: string } | null
    percorso: string | null; motivo: string | null; esito: string | null; conflitti: Conflitto[]
    serie: { quante: number; posizione: number } | null
  } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    api.get<NonNullable<typeof d>>(`/agenda/appuntamenti/${id}`).then(setD).catch((e) => setErrore(messaggioErrore(e)))
  }, [id])
  return (
    <Modale titolo={d?.persona ? `Appuntamento con ${d.persona.nome} ${d.persona.cognome}` : 'Appuntamento'} chiudi={chiudi}
            azioni={d?.persona && <Link className="bottone bottone-primario" to={`/persone/${d.persona.id}?linguetta=ricontatti`}>Apri la scheda</Link>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!d && !errore && <Attesa />}
      {d && (
        <>
          {d.conflitti.map((c) => <Avviso key={c.codice} tipo="attenzione">{c.messaggio}</Avviso>)}
          <dl className="calendario-dettaglio">
            <dt>Quando</dt><dd>{giornoInParole(d.giorno, true)}, {d.dalle}-{d.alle} ({durataLeggibile(d.durata_minuti)})</dd>
            <dt>Dove</dt><dd>{[d.sede_nome, d.risorsa_nome].filter(Boolean).join(' · ') || 'non indicato'}</dd>
            <dt>Lo segue</dt><dd>{d.responsabile ?? 'non indicato'}</dd>
            <dt>Stato</dt><dd>{d.stato === 'programmato' ? 'in programma' : d.stato.replace('_', ' ')}</dd>
            {d.serie && <><dt>Ripetizione</dt><dd>Il {d.serie.posizione}º di {d.serie.quante}</dd></>}
            {d.percorso && <><dt>Percorso</dt><dd>{d.percorso}</dd></>}
            {d.motivo && <><dt>Motivo</dt><dd>{d.motivo}</dd></>}
            {d.esito && <><dt>Esito</dt><dd>{d.esito}</dd></>}
          </dl>
          {!d.persona && <p className="tenue piccolo">La persona non è fra quelle che segui: il nome e il motivo li vede chi ha la scheda.</p>}
        </>
      )}
    </Modale>
  )
}

/**
 * Un impegno dell'agenda, nella volta del giorno scelto. Chi lo puo' modificare: «Modifica», «Annulla questa
 * volta» (se si ripete), «Ripristina questa volta», «Annulla» tutto.
 */
export function DettaglioEvento({ id, giorno, dati, chiudi, fatto }: {
  id: string; giorno: string; dati: RispostaAgenda; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [d, setD] = useState<DettaglioEventoApi | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [modifica, setModifica] = useState(false)
  useEffect(() => {
    api.get<DettaglioEventoApi>(`/agenda/eventi/${id}?giorno=${giorno}`).then(setD).catch((e) => setErrore(messaggioErrore(e)))
  }, [id, giorno])

  async function esegui(chiave: string, azione: () => Promise<unknown>, testo: string) {
    setErrore(null); setInCorso(chiave)
    try { await azione(); fatto(testo) } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(null) }
  }

  if (modifica && d) {
    return <ModuloEvento evento={d} iniziali={{ giorno: d.giorno, sede_id: d.sede_id }} dati={dati} mia={!d.sede_id}
                         utenteId="" chiudi={() => setModifica(false)} fatto={fatto} />
  }
  const titolo = d ? (d.tipo === 'turno' ? `Turno: ${d.persona ?? ''}` : d.titolo ?? NOMI_TIPO[d.tipo]) : 'Impegno'
  const volta = d?.volta
  return (
    <Modale titolo={titolo} chiudi={chiudi}
            azioni={d?.puo_modificare && <>
              {d.ripetizione && !d.volta_annullata && (
                <Bottone variante="discreto" inCorso={inCorso === 'volta'}
                         onClick={() => void esegui('volta', () => api.put(`/agenda/eventi/${id}/occorrenze/${giorno}`, { annullata: true }),
                           `Annullato ${giornoInParole(giorno)}; le altre volte restano.`)}>Annulla questa volta</Bottone>
              )}
              {d.ripetizione && (volta?.modificata || d.volta_annullata) && (
                <Bottone variante="discreto" inCorso={inCorso === 'ripristina'}
                         onClick={() => void esegui('ripristina', () => api.elimina(`/agenda/eventi/${id}/occorrenze/${giorno}`),
                           `${giornoInParole(giorno)} torna come le altre volte.`)}>Ripristina questa volta</Bottone>
              )}
              <Bottone variante="critico" inCorso={inCorso === 'annulla'}
                       onClick={() => void esegui('annulla', () => api.post(`/agenda/eventi/${id}/annulla`),
                         d.ripetizione ? 'Annullato, con tutte le volte.' : 'Annullato.')}>
                {d.ripetizione ? 'Annulla tutte le volte' : 'Annulla'}
              </Bottone>
              {d.tipo !== 'indisponibile' && <Bottone variante="primario" onClick={() => setModifica(true)}>Modifica</Bottone>}
            </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!d && !errore && <Attesa />}
      {d && (
        <dl className="calendario-dettaglio">
          <dt>Che cosa</dt><dd>{d.tipo === 'evento' && !d.sede_id ? 'Impegno personale' : NOMI_TIPO[d.tipo]}{d.funzione ? ` · ${d.funzione}` : ''}</dd>
          <dt>Quando</dt>
          <dd>
            {giornoInParole(giorno, true)}, {d.tutto_il_giorno ? 'tutto il giorno' : `${volta?.dalle ?? d.dalle}-${volta?.alle ?? d.alle}`}
            {volta?.modificata && <> <Etichetta tono="attenzione">cambiato solo per questo giorno</Etichetta></>}
            {d.volta_annullata && <> <Etichetta>annullato questo giorno</Etichetta></>}
          </dd>
          {d.ripetizione_in_parole && <><dt>Si ripete</dt><dd>{d.ripetizione_in_parole}</dd></>}
          {d.sede_nome && <><dt>Sede</dt><dd>{d.sede_nome}</dd></>}
          {d.risorsa_nome && <><dt>Stanza</dt><dd>{d.risorsa_nome}</dd></>}
          {d.persona && <><dt>Chi</dt><dd>{volta?.sostituto ? `${volta.sostituto} (al posto di ${d.persona})` : d.persona}</dd></>}
          {d.attivita && <><dt>Attività collettiva</dt><dd>{d.attivita}</dd></>}
          {d.progetto && <><dt>Progetto</dt><dd>{d.progetto}</dd></>}
          {d.partecipanti.length > 0 && <><dt>Partecipanti</dt><dd>{d.partecipanti.map((p) => `${p.nome} ${p.cognome}`).join(', ')}</dd></>}
          {d.note && <><dt>Note</dt><dd>{d.note}</dd></>}
          {d.sede_id && <><dt>Chi lo vede</dt><dd>{d.visibilita === 'sede' ? 'Chi lavora nella sede' : 'Solo i partecipanti; gli altri vedono la stanza occupata'}</dd></>}
          {d.creato_da && <><dt>Fissato da</dt><dd>{d.creato_da}</dd></>}
        </dl>
      )}
    </Modale>
  )
}

// --- modulo dell'impegno --------------------------------------------------------------------------------

type Collega = { id: string; nome: string; cognome: string }

/**
 * Nuovo impegno, o modifica di uno esistente (`evento`). Il tipo dice che cosa serve: la prenotazione la
 * stanza, il turno la persona e la funzione, riunione ed evento il titolo. Chi non scrive nell'agenda delle
 * sedi (563: mediatori, collaboratori esterni, sola lettura) fissa solo impegni suoi: «Aggiungi un impegno»,
 * con titolo, giorno, ore, ripetizione e note, senza sede e senza inviti. Sede chiusa: avviso con «Fissalo
 * lo stesso»; persona del turno non disponibile: avviso con «Programma lo stesso». Stanza occupata: l'API
 * rifiuta, e si sceglie un'altra stanza o un altro orario.
 */
export function ModuloEvento({ evento, iniziali, dati, mia, chiudi, fatto }: {
  evento?: DettaglioEventoApi; iniziali: InizialiEvento; dati: RispostaAgenda; mia: boolean; utenteId: string
  chiudi: () => void; fatto: (testo: string) => void
}) {
  const tipiPossibili: TipoEvento[] = [
    ...(dati.puo.sedi_scrittura.length ? ['riunione', 'prenotazione', 'attivita'] as TipoEvento[] : []),
    ...(dati.puo.sedi_turni.length ? ['turno'] as TipoEvento[] : []),
    ...(dati.puo.sedi_scrittura.length || dati.puo.propri ? ['evento'] as TipoEvento[] : []),
  ]
  // Solo impegni propri: niente sede, stanza, inviti; il modulo e' «Aggiungi un impegno».
  const soloPropri = !dati.puo.sedi_scrittura.length && !dati.puo.sedi_turni.length
  const invitare = dati.puo.invitare
  const personaIniziale = iniziali.persona
  const [tipo, setTipo] = useState<TipoEvento>((evento?.tipo as TipoEvento | undefined)
    ?? (personaIniziale && tipiPossibili.includes('turno') ? 'turno' : iniziali.risorsa_id ? 'prenotazione' : tipiPossibili[0] ?? 'riunione'))
  const sediDelTipo = tipo === 'turno' ? dati.puo.sedi_turni : dati.puo.sedi_scrittura
  const [sede, setSede] = useState(evento ? evento.sede_id ?? '' : mia && !iniziali.sede_id ? '' : iniziali.sede_id ?? sediDelTipo[0]?.id ?? '')
  const [titolo, setTitolo] = useState(evento?.titolo ?? '')
  const [note, setNote] = useState(evento?.note ?? '')
  const [stanza, setStanza] = useState(evento?.risorsa_id ?? iniziali.risorsa_id ?? '')
  const [persona, setPersona] = useState(evento ? (evento.utente_id ? `u:${evento.utente_id}` : evento.mediatore_id ? `m:${evento.mediatore_id}` : '')
    : personaIniziale ?? '')
  const [funzione, setFunzione] = useState(evento?.funzione_codice ?? '')
  const [giorno, setGiorno] = useState(evento?.giorno ?? iniziali.giorno)
  const [tutto, setTutto] = useState(evento?.tutto_il_giorno ?? false)
  const [dalle, setDalle] = useState(evento?.dalle ?? iniziali.dalle ?? '09:00')
  const [alle, setAlle] = useState(evento?.alle ?? (iniziali.dalle ? oraPiu(iniziali.dalle, 60) : '10:00'))
  const r = evento?.ripetizione
  const [frequenza, setFrequenza] = useState<string>(r?.frequenza ?? '')
  const [intervallo, setIntervallo] = useState(String(r?.intervallo ?? 1))
  const [giorni, setGiorni] = useState<string[]>((r?.giorni_settimana ?? [giornoIso(evento?.giorno ?? iniziali.giorno)]).map(String))
  const [fine, setFine] = useState<'fino_al' | 'volte'>(r?.volte ? 'volte' : 'fino_al')
  const [finoAl, setFinoAl] = useState(r?.fino_al ?? '')
  const [volte, setVolte] = useState(String(r?.volte ?? 10))
  const [saltaChiusure, setSaltaChiusure] = useState(r?.salta_chiusure ?? true)
  const [visibilita, setVisibilita] = useState<'sede' | 'partecipanti'>(evento?.visibilita ?? 'sede')
  const [partecipanti, setPartecipanti] = useState<Collega[]>(evento?.partecipanti ?? [])
  const [cerca, setCerca] = useState('')
  const [trovati, setTrovati] = useState<Collega[]>([])
  const [persone, setPersone] = useState<RispostaAgenda['persone']>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [avviso, setAvviso] = useState<{ codice: CodiceAvviso; messaggio: string } | null>(null)
  // Gli avvisi gia' confermati restano finche' si risponde agli avvisi: dopo «Fissalo lo stesso» puo' arrivare
  // «non disponibile», e viceversa. «Fissa» riparte da capo.
  const [conferme, setConferme] = useState<Conferme>(NESSUNA_CONFERMA)
  const [inCorso, setInCorso] = useState(false)

  // Chi puo' andare in turno nella sede scelta.
  useEffect(() => {
    if (tipo !== 'turno' || !sede) { setPersone([]); return }
    api.get<{ persone: RispostaAgenda['persone'] & { id: string }[] }>(`/agenda/persone?sede=${sede}`)
      .then((x) => setPersone(x.persone)).catch(() => setPersone([]))
  }, [tipo, sede])
  // Colleghi da invitare, dal nome.
  useEffect(() => {
    if (cerca.trim().length < 2) { setTrovati([]); return }
    const t = setTimeout(() => {
      api.get<Collega[]>(`/agenda/colleghi?cerca=${encodeURIComponent(cerca.trim())}`).then(setTrovati).catch(() => setTrovati([]))
    }, 300)
    return () => clearTimeout(t)
  }, [cerca])

  const senzaSede = !sede
  async function salva(date: Conferme) {
    setErrore(null); setAvviso(null); setInCorso(true); setConferme(date)
    const ripetizione = frequenza ? {
      frequenza, intervallo: Number(intervallo) || 1,
      giorni_settimana: frequenza === 'settimanale' ? giorni.map(Number) : null,
      ...(fine === 'volte' ? { volte: Number(volte) } : { fino_al: finoAl || null }), salta_chiusure: saltaChiusure,
    } : null
    const corpo: Record<string, unknown> = {
      risorsa_id: stanza || null, titolo: titolo.trim() || null, note: note.trim() || null, giorno,
      tutto_il_giorno: tutto, dalle: tutto ? null : dalle, alle: tutto ? null : alle, ripetizione,
      visibilita: senzaSede ? 'partecipanti' : visibilita, partecipanti: partecipanti.map((p) => p.id),
      conferma_fuori_orario: date.fuori_orario, conferma_non_disponibile: date.non_disponibile,
      ...(tipo === 'turno' ? {
        utente_id: persona.startsWith('u:') ? persona.slice(2) : null,
        mediatore_id: persona.startsWith('m:') ? persona.slice(2) : null, funzione_codice: funzione || null,
      } : {}),
    }
    try {
      if (evento) {
        const x = await api.patch<{ modificato: boolean; volte?: number }>(`/agenda/eventi/${evento.id}`, corpo)
        fatto(x.modificato ? 'Modifiche salvate.' : 'Nessuna modifica da salvare.')
      } else {
        const x = await api.post<{ volte: number; avvisati: number }>('/agenda/eventi', { ...corpo, tipo, sede_id: sede || null })
        if (soloPropri) {
          fatto(`Impegno aggiunto alla tua agenda${x.volte > 1 ? `: ${x.volte} volte` : ''}. Lo vedi solo tu.`)
          return
        }
        fatto([`${NOMI_TIPO[tipo]} fissat${tipo === 'turno' || tipo === 'evento' ? 'o' : 'a'}${x.volte > 1 ? `: ${x.volte} volte` : ''}.`,
          x.avvisati ? (tipo === 'turno' ? 'La persona riceve un avviso.' : 'I partecipanti ricevono un invito.') : ''].filter(Boolean).join(' '))
      }
    } catch (e) {
      const codice = e instanceof ErroreApi ? avvisoSuperabile(e.codice) : null
      if (codice) setAvviso({ codice, messaggio: (e as ErroreApi).message })
      else setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  const pronto = !!giorno && (tutto || (/^\d{2}:\d{2}$/.test(dalle) && /^\d{2}:\d{2}$/.test(alle)))
    && (tipo !== 'turno' || (!!sede && !!persona)) && (tipo !== 'prenotazione' || (!!sede && !!stanza))
    && (!['riunione', 'evento'].includes(tipo) || !!titolo.trim())
  return (
    <Modale larga titolo={evento ? `Modifica: ${soloPropri ? 'impegno' : NOMI_TIPO[tipo].toLowerCase()}` : soloPropri ? 'Aggiungi un impegno' : 'Nuovo impegno'}
            chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!pronto} onClick={() => void salva(NESSUNA_CONFERMA)}>
                {evento ? 'Salva le modifiche' : 'Fissa'}
              </Bottone>
            </>}>
      {soloPropri && !evento && (
        <p className="tenue">Un impegno tuo: lo vedi solo tu, nella tua agenda. Per una riunione con i colleghi chiedi a chi
          coordina la sede.</p>
      )}
      <div className="griglia-modulo">
        {!evento && !soloPropri && (
          <Scelta etichetta="Che cosa" valore={tipo} cambia={(v) => { setTipo(v as TipoEvento); setStanza('') }}
                  opzioni={tipiPossibili.map((t) => ({ valore: t, etichetta: NOMI_TIPO[t] }))} />
        )}
        {!evento && !soloPropri && (
          <Scelta etichetta="Sede" valore={sede} cambia={(v) => { setSede(v); setStanza(''); setPersona('') }}
                  vuota={tipo === 'evento' || tipo === 'riunione' ? '— nessuna: impegno personale —' : undefined}
                  opzioni={sediDelTipo.map((s) => ({ valore: s.id, etichetta: s.nome }))} />
        )}
      </div>
      {tipo === 'turno' && (
        <div className="griglia-modulo">
          <Scelta etichetta="Chi" obbligatorio valore={persona} cambia={setPersona} vuota="— scegli —"
                  opzioni={persone.map((p) => ({ valore: p.chiave, etichetta: `${p.cognome} ${p.nome}`,
                    gruppo: p.tipo === 'mediatore' ? 'Mediatori dell’albo' : 'Personale della sede' }))}
                  aiuto="Il personale con un incarico sulla sede e i mediatori che ci lavorano, anche se non usano il gestionale." />
          <Scelta etichetta="Funzione" valore={funzione} cambia={setFunzione} vuota="— nessuna —"
                  opzioni={dati.funzioni.map((f) => ({ valore: f.codice, etichetta: f.nome }))} />
        </div>
      )}
      {tipo !== 'turno' && (
        <Campo etichetta="Titolo" obbligatorio={tipo === 'riunione' || tipo === 'evento'} valore={titolo} cambia={setTitolo} maxLength={200}
               aiuto={soloPropri
                 ? 'Lo leggi solo tu: nel promemoria del giorno prima si legge «impegno», senza titolo. Non scrivere nomi di persone assistite.'
                 : 'Lo leggono i partecipanti e, se l’impegno è visibile alla sede, chi ci lavora. Non scrivere nomi di persone assistite.'} />
      )}
      <div className="griglia-modulo">
        <Campo tipo="date" etichetta="Giorno" obbligatorio valore={giorno} min={evento ? undefined : oggiARoma()} cambia={setGiorno} />
        {!tutto && <Campo tipo="time" etichetta="Dalle" obbligatorio valore={dalle} cambia={setDalle} passi={900} />}
        {!tutto && <Campo tipo="time" etichetta="Alle" obbligatorio valore={alle} cambia={setAlle} passi={900} />}
      </div>
      <Spunta etichetta="Tutto il giorno" valore={tutto} cambia={setTutto} />
      <SceltaStanza sedeId={sede || null} valore={stanza} cambia={setStanza} soloPrenotabili={tipo === 'prenotazione'}
                    obbligatorio={tipo === 'prenotazione'} />

      <div className="griglia-modulo">
        <Scelta etichetta="Si ripete" valore={frequenza} cambia={setFrequenza} vuota="No" opzioni={FREQUENZE} />
        {frequenza && <Campo tipo="number" etichetta="Ogni quante" valore={intervallo} cambia={setIntervallo} min={1} max={52}
                             aiuto="1: ogni volta; 2: una sì e una no." />}
      </div>
      {frequenza === 'settimanale' && <Caselle voci={GIORNI} scelte={giorni} cambia={setGiorni} />}
      {frequenza && (
        <div className="griglia-modulo">
          <Scelta etichetta="Fino a" valore={fine} cambia={(v) => setFine(v as 'fino_al' | 'volte')}
                  opzioni={[{ valore: 'fino_al', etichetta: 'Un giorno' }, { valore: 'volte', etichetta: 'Un numero di volte' }]} />
          {fine === 'fino_al'
            ? <Campo tipo="date" etichetta="Ultimo giorno" obbligatorio valore={finoAl} min={giorno} cambia={setFinoAl}
                     aiuto="Al più due anni." />
            : <Campo tipo="number" etichetta="Quante volte" obbligatorio valore={volte} cambia={setVolte} min={2} max={200}
                     aiuto="Al più 200." />}
        </div>
      )}
      {frequenza && (
        <Spunta etichetta="Salta festività e chiusure della sede" valore={saltaChiusure} cambia={setSaltaChiusure}
                aiuto="Le volte che cadono in una festa nazionale o in una chiusura non si fanno; contano comunque nel numero di volte." />
      )}

      {tipo !== 'turno' && sede && (
        <Scelta etichetta="Chi lo vede" valore={visibilita} cambia={(v) => setVisibilita(v as 'sede' | 'partecipanti')}
                opzioni={[{ valore: 'sede', etichetta: 'Chi lavora nella sede' },
                  { valore: 'partecipanti', etichetta: 'Solo i partecipanti (gli altri vedono la stanza occupata)' }]} />
      )}
      {tipo !== 'turno' && invitare && (
        <>
          <Campo tipo="search" etichetta="Invita un collega" valore={cerca} cambia={setCerca} segnaposto="Nome o cognome"
                 aiuto="Riceve un avviso, senza il titolo." />
          {trovati.filter((t) => !partecipanti.some((p) => p.id === t.id)).slice(0, 6).map((t) => (
            <Bottone key={t.id} variante="discreto" dimensione="piccolo"
                     onClick={() => { setPartecipanti([...partecipanti, t]); setCerca('') }}>+ {t.cognome} {t.nome}</Bottone>
          ))}
          {partecipanti.length > 0 && (
            <p className="riga">
              {partecipanti.map((p) => (
                <Bottone key={p.id} variante="secondario" dimensione="piccolo" aria-label={`Togli ${p.nome} ${p.cognome}`}
                         onClick={() => setPartecipanti(partecipanti.filter((x) => x.id !== p.id))}>{p.nome} {p.cognome} ×</Bottone>
              ))}
            </p>
          )}
        </>
      )}
      <AreaTesto etichetta="Note" valore={note} cambia={setNote} maxLength={2000} />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {avviso && (
        <Avviso tipo="attenzione">
          {avviso.messaggio}
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso}
                     onClick={() => void salva(conferma(conferme, avviso.codice))}>
              {avviso.codice === 'FUORI_ORARIO' ? 'Fissalo lo stesso' : 'Programma lo stesso'}
            </Bottone>
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAvviso(null)}>
              {avviso.codice === 'FUORI_ORARIO' ? 'Scelgo un altro orario' : 'Scelgo un’altra persona o un altro orario'}
            </Bottone>
          </div>
        </Avviso>
      )}
    </Modale>
  )
}

/**
 * Avvisi che si superano confermando: la sede chiusa (556) e la persona del turno che si e' segnata non
 * disponibile (decisione di Davide del 3/11/2026). La stanza occupata invece blocca.
 */
export type CodiceAvviso = 'FUORI_ORARIO' | 'PERSONA_NON_DISPONIBILE'
export type Conferme = { fuori_orario: boolean; non_disponibile: boolean }
export const NESSUNA_CONFERMA: Conferme = { fuori_orario: false, non_disponibile: false }
export const avvisoSuperabile = (codice: string | undefined): CodiceAvviso | null =>
  (codice === 'FUORI_ORARIO' || codice === 'PERSONA_NON_DISPONIBILE' ? codice : null)
/** Le conferme date piu' quella dell'avviso appena mostrato. */
export const conferma = (date: Conferme, codice: CodiceAvviso): Conferme =>
  (codice === 'FUORI_ORARIO' ? { ...date, fuori_orario: true } : { ...date, non_disponibile: true })
/** Il corpo delle conferme per l'API. */
export const corpoConferme = (c: Conferme) => ({ conferma_fuori_orario: c.fuori_orario, conferma_non_disponibile: c.non_disponibile })

const oraPiu = (o: string, m: number) => {
  const t = Math.min(Number(o.slice(0, 2)) * 60 + Number(o.slice(3, 5)) + m, 1440)
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
}

// --- indisponibilita' -----------------------------------------------------------------------------------

/**
 * «Segnati non disponibile» (decisione 5): un giorno o un periodo, tutto il giorno o in certe ore. Niente
 * motivo: non si chiede e non si conserva. La vede chi programma i turni, come «Non disponibile».
 */
export function ModuloIndisponibilita({ chiudi, fatto }: { chiudi: () => void; fatto: (testo: string) => void }) {
  const [dal, setDal] = useState(oggiARoma())
  const [al, setAl] = useState('')
  const [parte, setParte] = useState(false)
  const [dalle, setDalle] = useState('09:00')
  const [alle, setAlle] = useState('13:00')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  async function salva() {
    setErrore(null); setInCorso(true)
    try {
      await api.post('/agenda/indisponibilita', { dal, al: al || null, ...(parte ? { dalle, alle } : {}) })
      fatto('Fatto: nell’agenda risulti non disponibile. Chi programma i turni lo vede, senza motivo.')
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }
  return (
    <Modale titolo="Segnati non disponibile" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>Segna</Bottone>
            </>}>
      <p className="tenue">Non serve dire il motivo, e non va scritto da nessuna parte: risulti solo «non disponibile».</p>
      <div className="griglia-modulo">
        <Campo tipo="date" etichetta="Dal" obbligatorio valore={dal} min={oggiARoma()} cambia={setDal} />
        <Campo tipo="date" etichetta="Al" valore={al} min={dal} cambia={setAl} aiuto="Vuoto: un giorno solo." />
      </div>
      <Spunta etichetta="Solo in certe ore" valore={parte} cambia={setParte} />
      {parte && (
        <div className="griglia-modulo">
          <Campo tipo="time" etichetta="Dalle" obbligatorio valore={dalle} cambia={setDalle} passi={900} />
          <Campo tipo="time" etichetta="Alle" obbligatorio valore={alle} cambia={setAlle} passi={900} />
        </div>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
